import React, { useState } from 'react'
import { Accordion, Button } from 'is24-corecss'
import { useTranslation } from 'react-i18next'

import MultimediaAddLink from './MultimediaAddLink'
import { useAppDispatch } from '../../../../../../app/hooks'
import { deleteAttachment, doUploadMultimediaLink } from '../../../../../../redux/thunks/multimedia/multimedia'
import { isValidHttpUrl, stringToUrl } from '../../../../../../utils/text/text-utils'
import { AttachmentData, AttachmentType } from '../../../../../../@types/types'
import MultimediaLinksList from './MultimediaLinksList'
import { IconS24Plus24 } from '@is24/cosma-ui-icons'

export type LinkState = {
  linkTitle: string
  isValidLinkTitle: boolean
  isEditedLinkTitle?: boolean
  linkAddress: string
  isValidLinkAddress: boolean
  isEditedLinkAddress?: boolean
}

const initialMultimediaLinkState = {
  linkTitle: '',
  isValidLinkTitle: true,
  isEditedLinkTitle: false,
  linkAddress: '',
  isValidLinkAddress: true,
  isEditedLinkAddress: false,
}

function includesAngleBrackets(value: string) {
  return value.includes('<') || value.includes('>')
}

export const validate = (property: string, value: string) => {
  if (property === 'linkAddress') {
    return (
      (!includesAngleBrackets(value) && isValidHttpUrl(value) && value.length > 3 && value.length <= 2000) ||
      value.length == 0
    )
  }
  return !includesAngleBrackets(value) && value.length <= 30
}

export type MultimediaLinksManagerProps = {
  attachments: AttachmentData[]
}

const MultimediaLinksManager: React.FC<MultimediaLinksManagerProps> = ({ attachments }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [state, setState] = useState<LinkState>(initialMultimediaLinkState)

  const linksAttachments = attachments.filter((item) => item.attachmentType === AttachmentType.LINK)

  const onBlur = (property: string, value: string) => {
    const validPropertyName = `isValid${property.charAt(0).toUpperCase() + property.slice(1)}`
    const editedPropertyName = `isEdited${property.charAt(0).toUpperCase() + property.slice(1)}`
    !state[editedPropertyName as keyof LinkState] &&
      setState((prevState) => ({
        ...prevState,
        [validPropertyName]: validate(property, value),
        [editedPropertyName]: true,
      }))
  }

  const setLink = (property: string, value: string) => {
    const validPropertyName = `isValid${property.charAt(0).toUpperCase() + property.slice(1)}`
    const editedPropertyName = `isEdited${property.charAt(0).toUpperCase() + property.slice(1)}`

    setState((prevState) => ({
      ...prevState,
      [property]: value,
      ...(state[editedPropertyName as keyof LinkState] && { [validPropertyName]: validate(property, value) }),
    }))
  }

  const doReset = () => setState(initialMultimediaLinkState)

  const addLink = async () => {
    const url = state.linkAddress.trim()
    const title = state.linkTitle.trim()
    //max length of title is 30 chars and the title is handled this way in CLB
    const usedTitle = title ? title : url.length <= 30 ? url : url.substring(0, 27).concat('...')
    await dispatch(doUploadMultimediaLink(usedTitle, stringToUrl(url).toString()))
    doReset()
  }

  const isButtonDisabled = () => {
    const isUrlEmpty = state.linkAddress.trim().length == 0
    return isUrlEmpty || !state.isValidLinkAddress || !state.isValidLinkTitle
  }

  const onClickDeleteIcon = async (id?: string) => id && (await dispatch(deleteAttachment(id)))

  return (
    <Accordion.Root id='link-manager' onToggle={doReset}>
      <Accordion.Item
        key={`${linksAttachments.length > 0 ? 'open' : 'closed'}-accordion-item`}
        defaultExpanded={linksAttachments.length > 0}
      >
        <Accordion.Header>
          {t('wizard.screen.multimedia.linksManager.accordionTitle', 'Links to websites/virtual tours, etc.')}
        </Accordion.Header>
        <Accordion.Content>
          <MultimediaLinksList linksAttachments={linksAttachments} onClick={onClickDeleteIcon} />
          <MultimediaAddLink
            linkTitle={state.linkTitle}
            isValidLinkTitle={state.isValidLinkTitle}
            linkAddress={state.linkAddress}
            isValidLinkAddress={state.isValidLinkAddress}
            onChange={setLink}
            onBlur={onBlur}
          />
          <div className='align-right margin-top'>
            <Button
              id='add-link-button'
              name='addLinkButton'
              textStyle={true}
              disabled={isButtonDisabled()}
              onClick={addLink}
            >
              <IconS24Plus24 className='margin-right-xs' inline />
              {t('wizard.screen.multimedia.linksManager.addLinkButton', 'Add link')}
            </Button>
          </div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  )
}

export default MultimediaLinksManager
